import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { MachineDataType, MachineryDataRequestActionTypes, SearchtActionTypes } from '../models/machine-management';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';

const page_name = 'machineryManagement';

export interface machineryManagementState {
  isMachineryManagementDataLoading: boolean;
  machineryManagementData: Array<MachineDataType>;
  machineryManagementDataTotalRowCount: number;
  machineryManagementDataPageNumber: number;
  machineryManagementError: boolean;
  isAddMachineLoading: boolean;
  isAddMachineSuccess: boolean;
  isAddMachineError: boolean;
  isUpdateMachineLoading: boolean;
  isUpdateMachineSuccess: boolean;
  isUpdateMachineError: boolean;
  isDeleteMachineLoading: boolean;
  isDeleteMachineSuccess: boolean;
  isDeleteMachineError: boolean;
  machineManagementMessage: String;
  isBulkUploadMachineLoading: boolean;
  isBulkUploadMachineSuccess: boolean;
  isBulkUploadMachineError: boolean;
  machineStatusCode: number;
  searchItem: undefined | string;
  machinePageName:string;
}

const initialState: machineryManagementState = {
  isMachineryManagementDataLoading: false,
  machineryManagementData: [],
  machineryManagementDataTotalRowCount: 0,
  machineryManagementDataPageNumber: 1,
  machineryManagementError: false,
  isAddMachineLoading: false,
  isAddMachineSuccess: false,
  isAddMachineError: false,
  isUpdateMachineLoading: false,
  isUpdateMachineSuccess: false,
  isUpdateMachineError: false,
  isDeleteMachineLoading: false,
  isDeleteMachineSuccess: false,
  isDeleteMachineError: false,
  machineManagementMessage: '',
  isBulkUploadMachineLoading: false,
  isBulkUploadMachineSuccess: false,
  isBulkUploadMachineError: true,
  machineStatusCode: 0,
  searchItem: '',
  machinePageName:'Machine'
};

const machineryManagementReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getMachineryManagementDataRequest: (state, action: MachineryDataRequestActionTypes) => {
      state.isMachineryManagementDataLoading = true;
      state.isAddMachineSuccess = false;
      state.isDeleteMachineSuccess = false;
      state.isUpdateMachineSuccess = false;           
      state.machinePageName = get(action, 'payload.pageName', 'Machine')
    },
    getMachineryManagementDataResponse: (state, action) => {
      state.isMachineryManagementDataLoading = false;
      if (state.searchItem === get(action, 'payload.search_item', '')) {
        state.machineryManagementData = get(action, 'payload.data', []);
        state.machineryManagementDataTotalRowCount = get(action, 'payload.total_count', []);
        state.machineryManagementDataPageNumber = get(action, 'payload.page', []);
      }
    },
    getMachineryManagementDataFailed: (state, action: any) => {
      state.isMachineryManagementDataLoading = false;
      state.machineryManagementError = true;
      state.machineryManagementData = [];

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message);
    },
    addMachineRequest: (state) => {
      state.isMachineryManagementDataLoading = true;
      state.isAddMachineLoading = true;
      state.isAddMachineSuccess = false;
      state.isAddMachineError = false;
    },
    addMachineResponse: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isAddMachineLoading = false;
      state.isAddMachineSuccess = true;
      state.machineStatusCode = action.payload;      
      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace('Machine',state.machinePageName));
    },
    addMachineFailed: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isAddMachineLoading = false;
      state.isAddMachineError = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace('Machine',state.machinePageName));
    },
    updateMachineRequest: (state) => {
      state.isMachineryManagementDataLoading = true;
      state.isUpdateMachineLoading = true;
      state.isUpdateMachineSuccess = false;
      state.isUpdateMachineError = false;
    },
    updateMachineResponse: (state, action: PayloadAction<any>) => {
      state.isUpdateMachineLoading = false;
      state.isMachineryManagementDataLoading = false;
      state.isUpdateMachineSuccess = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace('Machine',state.machinePageName));
    },
    updateMachineFailed: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isUpdateMachineLoading = false;
      state.isUpdateMachineError = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace('Machine',state.machinePageName));
    },
    deleteMachineRequest: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = true;
      state.isDeleteMachineLoading = true;
      state.isDeleteMachineSuccess = false;
      state.isDeleteMachineError = false;
    },
    deleteMachineResponse: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isDeleteMachineLoading = false;
      state.isDeleteMachineSuccess = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace('Machine',state.machinePageName));
    },
    deleteMachineFailed: (state, action: any) => {
      state.isMachineryManagementDataLoading = false;
      state.isDeleteMachineLoading = false;
      state.isDeleteMachineError = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(message.replace('Machine',state.machinePageName));
    },
    bulkUploadRequest: (state) => {
      state.isMachineryManagementDataLoading = true;
      state.isBulkUploadMachineLoading = true;
      state.isBulkUploadMachineSuccess = false;
      state.isBulkUploadMachineError = false;
    },
    bulkUploadResponse: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isBulkUploadMachineLoading = false;
      state.isBulkUploadMachineSuccess = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.success(message.replace('Machine',state.machinePageName));
    },
    bulkUploadFailed: (state, action: PayloadAction<any>) => {
      state.isMachineryManagementDataLoading = false;
      state.isBulkUploadMachineLoading = false;
      state.isBulkUploadMachineError = true;
      state.machineStatusCode = action.payload;

      const message = getStatusMessage(page_name, action.payload);
      Snackbar.error(
        action.payload === '409'
          ? '"MACHINE NAME" already exists, Please specify alternative "MACHINE NAME" in the Template file.'.replace('MACHINE',state.machinePageName.toUpperCase())
          : message.replace('Machine',state.machinePageName)
      );
    },
    resetLoadingState: (state) => {
      state.machineManagementMessage = '';
      state.isAddMachineSuccess = false;
      state.isAddMachineError = false;
      state.isDeleteMachineSuccess = false;
      state.isUpdateMachineSuccess = false;
      state.isUpdateMachineError = false;
      state.isDeleteMachineError = false;
      state.machineryManagementError = false;
      state.machineStatusCode = 0;
      state.isBulkUploadMachineLoading = false;
      state.isBulkUploadMachineSuccess = false;
      state.isBulkUploadMachineError = true;
    },
    resetMachineryManagementData: (state) => {
      state.machineryManagementData = [];
      state.machineryManagementDataTotalRowCount = 0;
      state.machineryManagementDataPageNumber = 1;
      state.searchItem = '';
    },
    onSearchForItem: (state, action: SearchtActionTypes) => {
      state.searchItem = action.payload;
    },
  },
});

export const machineryManagementActions = machineryManagementReducer.actions;
export default machineryManagementReducer;
